import type { KeyboardEvent, ReactNode } from "react";
import { Button } from "./Button";
import { Card } from "./Card";
import { Eyebrow } from "./Eyebrow";

export interface PromptCardProps {
    eyebrow: string;
    title: string;
    meta?: ReactNode;
    actionLabel: string;
    onAction: () => void;
    align?: "start" | "center";
    /** CTA height: `xl` (48px, default: every couple-facing CTA) or `lg` (40px). */
    actionSize?: "lg" | "xl";
    children?: ReactNode;
}

function activateOnKeyboard(event: KeyboardEvent<HTMLButtonElement>, action: () => void) {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        action();
    }
}

export function PromptCard({
    eyebrow,
    title,
    meta,
    actionLabel,
    onAction,
    align = "start",
    actionSize = "xl",
    children,
}: PromptCardProps) {
    const centered = align === "center";
    return (
        <section className="flex flex-col gap-2">
            <Eyebrow as="div">{eyebrow}</Eyebrow>
            <Card
                role="article"
                className={[
                    "flex flex-col gap-4 p-6",
                    centered ? "items-center text-center" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {children ? <div className={centered ? "flex justify-center" : ""}>{children}</div> : null}
                <div className="flex flex-col gap-2">
                    <h2 className="m-0 [font:var(--type-h2)] text-primary">{title}</h2>
                    {meta ? <div className="[font:var(--type-body)] text-secondary">{meta}</div> : null}
                </div>
                <Button
                    full
                    size={actionSize}
                    onClick={onAction}
                    onKeyDown={(event) => activateOnKeyboard(event, onAction)}
                >
                    {actionLabel}
                </Button>
            </Card>
        </section>
    );
}
